'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from '@/providers/auth-provider'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { generateNextExternalId } from '@/lib/utils/external-id'

// Onboarding steps and user progress tracking
export interface OnboardingProgress {
  currentStep: 'welcome' | 'profile' | 'golf-preferences' | 'location' | 'notifications' | 'tutorial' | 'completed'
  completedSteps: string[]
  profileData: {
    displayName?: string
    bio?: string
    profileImage?: string
  }
  golfPreferences: {
    handicap?: string
    playStyle?: string[]
    preferredTimes?: string[]
  }
  permissions: {
    location: 'granted' | 'denied' | 'pending' | null
    notifications: 'granted' | 'denied' | 'pending' | null
  }
  completedAt: Date | null
  skipped: boolean
}

interface OnboardingContextType {
  progress: OnboardingProgress | null
  isOnboardingRequired: boolean
  isLoading: boolean
  updateProgress: (updates: Partial<OnboardingProgress>) => Promise<void>
  completeOnboarding: () => Promise<void>
  skipOnboarding: () => Promise<void>
  resetOnboarding: () => Promise<void>
}

const defaultProgress: OnboardingProgress = {
  currentStep: 'welcome',
  completedSteps: [],
  profileData: {},
  golfPreferences: {},
  permissions: {
    location: null,
    notifications: null
  },
  completedAt: null,
  skipped: false
}

const OnboardingContext = createContext<OnboardingContextType>({
  progress: null,
  isOnboardingRequired: false,
  isLoading: true,
  updateProgress: async () => {},
  completeOnboarding: async () => {},
  skipOnboarding: async () => {},
  resetOnboarding: async () => {}
})

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [progress, setProgress] = useState<OnboardingProgress | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Load onboarding progress from Firestore
  useEffect(() => {
    async function loadProgress() {
      if (authLoading) return
      
      if (!user || user.isAnonymous) {
        setProgress(null)
        setIsLoading(false)
        return
      }

      try {
        if (!db) {
          console.warn('OnboardingProvider: Firestore db not initialized, using default progress')
          setProgress(defaultProgress)
          setIsLoading(false)
          return
        }
        const onboardingRef = doc(db, 'userOnboarding', user.id)
        const onboardingDoc = await getDoc(onboardingRef)

        if (onboardingDoc.exists()) {
          const data = onboardingDoc.data()
          setProgress({
            ...defaultProgress,
            ...data,
            completedAt: data.completedAt ? data.completedAt.toDate() : null
          })
        } else {
          // First time user - create default progress
          setProgress(defaultProgress)
          await setDoc(onboardingRef, {
            ...defaultProgress,
            createdAt: new Date(),
            userId: user.id
          })
        }
      } catch (error) {
        console.error('Error loading onboarding progress:', error)
        setProgress(defaultProgress)
      } finally {
        setIsLoading(false)
      }
    }

    loadProgress()
  }, [user, authLoading])

  // Determine if onboarding is required
  const isOnboardingRequired = Boolean(
    user && 
    !user.isAnonymous && 
    progress && 
    progress.currentStep !== 'completed' && 
    !progress.skipped
  )

  // Update progress in both state and Firestore
  const updateProgress = async (updates: Partial<OnboardingProgress>) => {
    if (!user || user.isAnonymous || !progress) return

    const newProgress = { ...progress, ...updates }
    setProgress(newProgress)

    try {
        if (!db) return
      const onboardingRef = doc(db, 'userOnboarding', user.id)
      await updateDoc(onboardingRef, {
        ...updates,
        updatedAt: new Date()
      })
    } catch (error) {
      console.error('Error updating onboarding progress:', error)
      // Revert state on error
      setProgress(progress)
      throw error
    }
  }

  // Complete onboarding and update user profile
  const completeOnboarding = async () => {
    if (!user || user.isAnonymous || !progress) return

    try {
      // Update onboarding status
      await updateProgress({
        currentStep: 'completed',
        completedAt: new Date(),
        completedSteps: [...progress.completedSteps, progress.currentStep]
      })

      // Update user profile in Firestore with onboarding data
        if (!db) return
      const userRef = doc(db, 'users', user.id)
      const userDoc = await getDoc(userRef)
      
      const profileUpdates: any = {
        onboardingCompleted: true,
        onboardingCompletedAt: new Date(),
        updatedAt: new Date()
      }

      // Merge profile data from onboarding
      if (progress.profileData.displayName) {
        profileUpdates.displayName = progress.profileData.displayName
      }
      if (progress.profileData.bio) {
        profileUpdates.bio = progress.profileData.bio
      }
      if (progress.golfPreferences.handicap) {
        profileUpdates.handicap = progress.golfPreferences.handicap
      }
      if (progress.golfPreferences.playStyle?.length) {
        profileUpdates.playStyle = progress.golfPreferences.playStyle
      }
      if (progress.golfPreferences.preferredTimes?.length) {
        profileUpdates.preferredTimes = progress.golfPreferences.preferredTimes
      }

      // Generate external ID if not present
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (!userData.externalId) {
          try {
            const externalId = await generateNextExternalId();
            profileUpdates.externalId = externalId;
          } catch (err) {
            console.error("Failed to generate external ID during onboarding completion", err);
            // Don't block onboarding completion on this, but log it
          }
        }
        await updateDoc(userRef, profileUpdates)
      } else {
        let externalId = undefined;
        try {
          externalId = await generateNextExternalId();
        } catch (err) {
          console.error("Failed to generate external ID during user creation", err);
        }

        await setDoc(userRef, {
          ...profileUpdates,
          userId: user.id,
          email: user.email,
          externalId,
          createdAt: new Date()
        })
      }

      console.log('Onboarding completed successfully')
    } catch (error) {
      console.error('Error completing onboarding:', error)
      throw error
    }
  }

  // Skip onboarding
  const skipOnboarding = async () => {
    if (!user || user.isAnonymous) return

    try {
      await updateProgress({
        skipped: true,
        completedAt: new Date()
      })
      
      console.log('Onboarding skipped')
    } catch (error) {
      console.error('Error skipping onboarding:', error)
      throw error
    }
  }

  // Reset onboarding (for testing/admin purposes)
  const resetOnboarding = async () => {
    if (!user || user.isAnonymous) return

    try {
        if (!db) return
      const onboardingRef = doc(db, 'userOnboarding', user.id)
      const resetProgress = {
        ...defaultProgress,
        userId: user.id,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      
      await setDoc(onboardingRef, resetProgress)
      setProgress(defaultProgress)
      
      console.log('Onboarding reset')
    } catch (error) {
      console.error('Error resetting onboarding:', error)
      throw error
    }
  }

  return (
    <OnboardingContext.Provider value={{
      progress,
      isOnboardingRequired,
      isLoading,
      updateProgress,
      completeOnboarding,
      skipOnboarding,
      resetOnboarding
    }}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider')
  }
  return context
}

export default OnboardingProvider
